'use client'

import {Suspense, useState, useSyncExternalStore, type ReactNode} from 'react'

const never = new Promise<never>(() => {})
const subscribeToNothing = () => () => {}

/** True while rendering on the server and during hydration; false for every client render after it. */
export const useIsHydrating = () => useSyncExternalStore(subscribeToNothing, () => false, () => true)

function Hold({release}: {release: boolean}) {
    const hydrating = useIsHydrating()
    // A thrown promise (not use()) because this component legitimately stops suspending once hydration is done.
    if (hydrating && !release && typeof window !== 'undefined') throw never
    return null
}

/**
 * Renders children on the server even when the browser must not show them yet, so instant-navigation validation can
 * see the page. While `release` is false during hydration the boundary keeps the server HTML dehydrated: no mismatch,
 * no effects. The first later update renders the subtree on the client instead of hydrating it, so callers keep
 * gated effects paused themselves (for example with a hidden Activity) until they release it.
 *
 * Server and browser agree on `release` at mount. A boundary that mounts released renders its children plainly: inside a
 * Suspense boundary they would hydrate after the parents' mount effects (for example the browser locale) and mismatch
 * the server HTML.
 */
export function HoldHydration({release, children}: {release: boolean, children: ReactNode}) {
    const [holds] = useState(!release)
    if (!holds) return <>{children}</>
    return (
        <Suspense fallback={null}>
            <Hold release={release}/>
            {children}
        </Suspense>
    )
}
